import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import {
    getOrderPromotions,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { checkoutSharedStyles } from "@tps/App/SharedStyles/CheckoutSharedStyles";
import { xScreen } from "@tps/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getPromotionsDataView(state, state.pages.orderConfirmation.cartId);
    let orderPromotions;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
    }
    return {
        orderPromotions,
    };
};

export interface OrderConfirmationPaymentInformationStyles {
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    paymentsInfoWrapper?: InjectableCss;
    poAndPromotionsWrapper?: InjectableCss;
    paymentMethodWrapper?: InjectableCss;
}

export const orderPaymentInformationStyles: OrderConfirmationPaymentInformationStyles = {
    paymentsInfoWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            flex-wrap: wrap;
            row-gap: 24px;
        `,
    },
    paymentMethodWrapper: {
        css: css`
            width: 430px;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    poAndPromotionsWrapper: {
        css: css`
            width: 430px;
            display: flex;
            flex-direction: column;
            row-gap: 20px;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = orderPaymentInformationStyles;

const OrderConfirmationPaymentInformation = ({ cart, orderPromotions }: Props) => {
    let orderPromorionsValues = "";
    orderPromotions?.forEach((orderPromotion, index) => {
        if (index === orderPromotions.length - 1) {
            orderPromorionsValues += `${orderPromotion.name}`;
        } else {
            orderPromorionsValues += `${orderPromotion.name}, `;
        }
    });

    return (
        <StyledWrapper>
            <Typography {...checkoutSharedStyles.sectionTitle}>{translate("Payment")}</Typography>
            <StyledWrapper {...styles.paymentsInfoWrapper}>
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <StyledWrapper {...styles.paymentMethodWrapper}>
                        <SmallHeadingAndText
                            {...checkoutSharedStyles.headingAndTextStyles}
                            heading={translate("Payment Method")}
                            text={cart.paymentMethod?.description || cart.paymentMethod?.name || ""}
                        />
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.poAndPromotionsWrapper}>
                    <SmallHeadingAndText
                        {...checkoutSharedStyles.headingAndTextStyles}
                        heading={translate("PO #")}
                        text={cart.poNumber}
                    />
                    {orderPromorionsValues && (
                        <SmallHeadingAndText
                            {...checkoutSharedStyles.headingAndTextStyles}
                            heading={translate("Promotion Code")}
                            text={orderPromorionsValues}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(OrderConfirmationPaymentInformation);
